/* generated using openapi-typescript-codegen -- do not edit */
/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { ContentMasterBulkReviewListView } from '../models/ContentMasterBulkReviewListView';
import type { MarketplaceCategoryListView } from '../models/MarketplaceCategoryListView';
import type { MarketplaceCategorySuggestionView } from '../models/MarketplaceCategorySuggestionView';
import type { MarketplaceListingGenerateSyncView } from '../models/MarketplaceListingGenerateSyncView';
import type { MarketplaceListingSubmitResultView } from '../models/MarketplaceListingSubmitResultView';
import type { MarketplaceMetadataRefreshResultView } from '../models/MarketplaceMetadataRefreshResultView';
import type { MarketplaceSyncStatusView } from '../models/MarketplaceSyncStatusView';
import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';
export class MarketplaceListingService {
    /**
     * Marketplace metadata sync freshness for a channel
     * Returns when the marketplace metadata was last refreshed and whether each sync is "fresh" (within
     * the freshness TTL), so the UI can show a green/stale indicator before launching a listing batch.
     *
     * Always includes the category-tree sync and the channel-attributes sync (SCX.tChannel), plus the
     * JTL-Worker heartbeat (informational — it does NOT gate the `fresh` flags). When `wawiArtikelId` or
     * `kCategory` is supplied, the response also includes per-category attribute freshness: `fresh=false`
     * with `attributeCount=0` means that category needs a `category_attributes` refresh
     * (`POST /refresh`) before it can be listed against.
     *
     * Read-only. Same `source` addressing as Content Master (`shop:N`).
     * @param source Channel identifier (shop:N)
     * @param wawiArtikelId Scope per-category freshness to this article's selected category
     * @param kCategory Scope per-category freshness to this category (overrides wawiArtikelId)
     * @param dataSourceId
     * @returns MarketplaceSyncStatusView Sync freshness
     * @throws ApiError
     */
    public static getAppMarketplaceListingSyncStatus(
        source: string,
        wawiArtikelId?: number,
        kCategory?: number,
        dataSourceId?: string,
    ): CancelablePromise<MarketplaceSyncStatusView> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v2/api/marketplace-listing/sync-status',
            query: {
                'source': source,
                'wawiArtikelId': wawiArtikelId,
                'kCategory': kCategory,
                'dataSourceId': dataSourceId,
            },
            errors: {
                400: `Invalid request (bad/platform-level source)`,
            },
        });
    }
    /**
     * Trigger a JTL-Wawi marketplace metadata refresh (fetch) for a channel
     * Queues a marketplace metadata refresh for the channel by inserting request rows into
     * `SCX.tMetaDataChanged` — the same thing the JTL-Wawi channel "Aktualisieren" UI does. The JTL-Worker
     * picks the request up and re-imports the metadata (e.g. category attributes), after which
     * `GET /sync-status` shows the updated refresh timestamps and a smaller `pendingRefresh.pending` count.
     *
     * This only SCHEDULES the fetch — it cannot start the JTL-Worker. If the worker is offline the request
     * just waits (check `GET /sync-status` -> `worker.alive`). Types map 1:1 to sync-status blocks:
     * `categories` -> `categoriesSync`, `channel_attributes` -> `attributesSync`, `category_attributes` ->
     * per-category attributes (`category` block). Omitting `types` queues ALL of them.
     *
     * T7: for `category_attributes` the response additionally carries `directSchemas` — schemas of the
     * channel's ASSIGNED categories that were missing or older than the staleness threshold are re-fetched
     * DIRECTLY from the marketplace (SCX Seller API) within this request, because the JTL-Worker only
     * re-refreshes categories of ACTIVE listings and never the rest. This direct part is already done when
     * the response returns (no worker involved).
     *
     * Same `source` addressing as the rest of the listing flow — a shop-level channel (`shop:N`).
     * @param requestBody
     * @returns MarketplaceMetadataRefreshResultView Refresh request(s) queued
     * @throws ApiError
     */
    public static postAppMarketplaceListingRefresh(
        requestBody: {
            /**
             * Channel identifier `shop:N`
             */
            source: string;
            /**
             * Refresh types to queue. Omit to queue all types.
             */
            types?: Array<'categories' | 'channel_attributes' | 'category_attributes'> | null;
            dataSourceId?: string | null;
        },
    ): CancelablePromise<MarketplaceMetadataRefreshResultView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v2/api/marketplace-listing/refresh',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid request (bad/platform-level source, unknown type, non-SCX channel)`,
            },
        });
    }
    /**
     * Generate marketplace listing field values for a set of products
     * For each `wawiArtikelId` a `content_master_product` (source = shop:N) is created/reused, set to
     * `listing_generate_queued`, and a Symfony Messenger task is dispatched onto the shared Content Master
     * `async_cm` transport (`bin/console messenger:consume async_cm`). There is NO batch entity.
     *
     * Per product the worker resolves the channel + selected category, reads the required/recommended
     * category attributes (SCX), generates their values with a per-marketplace prompt, and stores them in
     * `content_master_field_version` (mpAttr_* keys, reusing Content Master version-control). The per-channel
     * product description (core `description` field) is ALWAYS generated too, regardless of the group flags,
     * and counts as one more billed field per product. Read-only against Wawi at this stage.
     *
     * Products already mid-generation (`listing_generate_queued` / `_in_progress`) are silently skipped.
     * Poll progress with the SAME endpoints as bulk review: status tabs via
     * `GET /content-master/products/local?processingStatus=listing_generate_*`, single product (with the
     * marketplace section) via `GET /content-master/product`.
     * @param requestBody
     * @returns MarketplaceListingSubmitResultView Products queued (may include silent skips)
     * @throws ApiError
     */
    public static postAppMarketplaceListingGenerate(
        requestBody: {
            /**
             * Channel identifier `shop:N` (marketplace; see GET /content-master/workspace-config — nTypLabel "Marktplatz")
             */
            source: string;
            /**
             * Active shop language: kSprache (integer or digit string) or ISO code
             */
            language: string;
            /**
             * Products to generate listing fields for (same shape as Content Master bulk generate)
             */
            items: Array<{
                wawiArtikelId: number;
            }>;
            /**
             * Override; omit to auto-detect from the channel
             */
            marketplace?: 'amazon' | 'otto' | 'kaufland' | 'metro' | 'cdiscount' | 'tiktok' | null;
            dataSourceId?: string | null;
            /**
             * Generate required ("Pflichtfelder") attributes. Default true when omitted.
             */
            generateRequired?: boolean | null;
            /**
             * Generate recommended attributes. Default false when omitted.
             */
            generateRecommended?: boolean | null;
            /**
             * Generate other (neither required nor recommended) attributes. Default false when omitted.
             */
            generateOther?: boolean | null;
            /**
             * When the article has no marketplace category selected, AI-detect and assign the best-fitting one before generating (ticket §4). Default false.
             */
            autoDetectCategory?: boolean | null;
            /**
             * Whether to AI-generate attributes that already carry a Wawi channel/category DEFAULT value ("Verkaufskanalfelder zuordnen", incl. DotLiquid templates — see `attributes[].defaultValue`). Default false: such attributes are skipped, Wawi keeps filling them itself.
             */
            generatePrefilled?: boolean | null;
            /**
             * "Fill only empty fields": skip attributes that already have a value in Wawi for this channel and skip the description when the channel has its own non-empty one; a product with nothing left to generate ends as listing_generate_done without an AI call. Ignored when an explicit `fields` list is given. Default false.
             */
            onlyEmptyFields?: boolean | null;
        },
    ): CancelablePromise<MarketplaceListingSubmitResultView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v2/api/marketplace-listing/generate',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid request (empty items, bad source/marketplace, etc.)`,
                403: `Forbidden. Possible \`errorCode\`:
                - \`content_master_subscription_insufficient\` — marketplace listing generation not included in the subscription plan
                - \`content_master_quota_exceeded\` — projected token usage exceeds budget`,
            },
        });
    }
    /**
     * Generate marketplace listing field values for ONE product synchronously
     * Synchronous single-product counterpart of `POST /generate`: the `content_master_product`
     * (source = shop:N) is created/reused and generation runs INSIDE the request (no queue), so the response
     * already reflects the final `listing_generate_done` / `listing_generate_failed` outcome. Generated
     * values are stored the same way (`content_master_field_version`, mpAttr_* keys + the core `description`
     * field) — read them afterwards from `GET /content-master/product` (`languageVersions[lang].draft`) or
     * `/marketplace-listing/review`.
     *
     * The per-channel product description (core `description` field) is ALWAYS generated alongside the
     * selected attribute groups and counts as one more billed field.
     *
     * `fields` ("regenerate one field"): pass an explicit list of field keys — mpAttr_* keys of the selected
     * category and/or `"description"` — to regenerate exactly those fields. The generate* group flags and the
     * prefilled skip are IGNORED then; the quota pre-flight prices exactly `fields.length` fields. A malformed
     * list answers 400; a key that is not part of the article's category fails the run
     * (`listing_generate_failed` with the exact reason in `errorMessage`).
     *
     * `autoDetectCategory` works here too. Because this is a live OpenAI call (chunked per attribute), a large
     * category may take 30s+ — set an adequate client timeout. Products already mid-generation
     * (`listing_generate_queued` / `_in_progress`) answer 409 without starting a second run.
     * @param requestBody
     * @returns MarketplaceListingGenerateSyncView Generation finished (done or failed) — status + summary + warnings
     * @throws ApiError
     */
    public static postAppMarketplaceListingGenerateSync(
        requestBody: {
            /**
             * Channel identifier `shop:N` (marketplace; see GET /content-master/workspace-config — nTypLabel "Marktplatz")
             */
            source: string;
            /**
             * Active shop language: kSprache (integer or digit string) or ISO code
             */
            language: string;
            /**
             * The single article to generate listing fields for
             */
            wawiArtikelId: number;
            /**
             * Override; omit to auto-detect from the channel
             */
            marketplace?: 'amazon' | 'otto' | 'kaufland' | 'metro' | 'cdiscount' | 'tiktok' | null;
            dataSourceId?: string | null;
            /**
             * Generate required ("Pflichtfelder") attributes. Default true when omitted.
             */
            generateRequired?: boolean | null;
            /**
             * Generate recommended attributes. Default false when omitted.
             */
            generateRecommended?: boolean | null;
            /**
             * Generate other (neither required nor recommended) attributes. Default false when omitted.
             */
            generateOther?: boolean | null;
            /**
             * When the article has no marketplace category selected, AI-detect and assign the best-fitting one before generating (ticket §4). Default false.
             */
            autoDetectCategory?: boolean | null;
            /**
             * Extra product information provided by the user, merged on the CLIENT into one plain-text string (free-text notes + text extracted from up to 3 files). Used only inside the AI prompt of THIS request as product data — never stored, not sent for bulk. Max 50000 characters; longer input is rejected with 400.
             */
            additionalContext?: string | null;
            /**
             * Whether to AI-generate attributes that already carry a Wawi channel/category DEFAULT value ("Verkaufskanalfelder zuordnen", incl. DotLiquid templates — see `attributes[].defaultValue`). Default false: such attributes are skipped, Wawi keeps filling them itself.
             */
            generatePrefilled?: boolean | null;
            /**
             * "Fill only empty fields": skip attributes that already have a value in Wawi for this channel and skip the description when the channel has its own non-empty one; a product with nothing left to generate ends as listing_generate_done without an AI call. Ignored when an explicit `fields` list is given. Default false.
             */
            onlyEmptyFields?: boolean | null;
            /**
             * Explicit field list ("regenerate one field"): mpAttr_* attribute keys of the selected category and/or "description" (per-channel product description). When set, the generate* group flags and the prefilled skip are IGNORED. Omit for normal group-based generation.
             */
            fields?: Array<string> | null;
        },
    ): CancelablePromise<MarketplaceListingGenerateSyncView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v2/api/marketplace-listing/generate-sync',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid request (bad source/marketplace, missing wawiArtikelId, etc.)`,
                403: `Forbidden. Possible \`errorCode\`:
                - \`content_master_subscription_insufficient\` — marketplace listing generation not included in the subscription plan
                - \`content_master_quota_exceeded\` — projected token usage exceeds budget`,
                409: `Product is already mid-generation (queued / in progress)`,
            },
        });
    }
    /**
     * Review generated marketplace listing attributes (original vs generated)
     * Marketplace counterpart of `GET /content-master/bulk/review`. Returns a page (≤20 products) of
     * products in `listing_generate_done`, each with its category attributes shown as ORIGINAL (current Wawi
     * value, is_current snapshot) vs GENERATED (AI draft), grouped by language, plus the attribute schema
     * (displayName/type/required/recommended/multiple/allowedOptions). The per-channel product description
     * is included as a `type = 'core'` row (`apiName = 'description'`, ordered first) — same shape as the CM
     * bulk review. Wawi-free: reads content_master_field_version + cachedConfig. `source` (shop:N) is
     * OPTIONAL — omit it to review products generated under ANY channel of the data source.
     * @param source Optional channel filter (shop:N). Omit to review products generated under ANY channel of the data source.
     * @param language Filter to one storage language (kSprache or ISO). Omit for all languages with stored versions.
     * @param page
     * @param limit
     * @param dataSourceId
     * @returns ContentMasterBulkReviewListView Review page (reuses the bulk-review model; marketplace attributes are `type=marketplace` field rows)
     * @throws ApiError
     */
    public static getAppMarketplaceListingReview(
        source?: string,
        language?: string,
        page: number = 1,
        limit: number = 20,
        dataSourceId?: string,
    ): CancelablePromise<ContentMasterBulkReviewListView> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v2/api/marketplace-listing/review',
            query: {
                'source': source,
                'language': language,
                'page': page,
                'limit': limit,
                'dataSourceId': dataSourceId,
            },
            errors: {
                400: `Invalid request`,
            },
        });
    }
    /**
     * Search the marketplace category tree of an SCX channel
     * Lists the channel's listable categories (`nListingAllowed=1`) so the user can pick/confirm a category
     * for an article that has none (the `category_not_selected` state). Read-only. SCX marketplaces only
     * (Otto/Kaufland/Metro/…); the `kCategory` of a result is what you send to `POST /assign-category`.
     *
     * Two modes:
     * - `q` — flat search by category name / parent path / marketplace category id.
     * - `parentKCategory` — lazy tree navigation: pass a category's `kCategory` to get its direct children,
     * or `0` for the top-level (root) categories. Each item has `hasChildren` (expandable) and
     * `kParentCategory`.
     * @param source Channel identifier (shop:N)
     * @param q Search term (category name / parent / id)
     * @param parentKCategory Tree navigation: direct children of this kCategory; 0 = top-level roots
     * @param limit
     * @param offset Pagination offset (0-based) — combine with the response `total`/`hasMore` for infinite scroll
     * @param dataSourceId
     * @returns MarketplaceCategoryListView Matching categories
     * @throws ApiError
     */
    public static getAppMarketplaceListingCategories(
        source: string,
        q?: string,
        parentKCategory?: number,
        limit: number = 50,
        offset?: number,
        dataSourceId?: string,
    ): CancelablePromise<MarketplaceCategoryListView> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/v2/api/marketplace-listing/categories',
            query: {
                'source': source,
                'q': q,
                'parentKCategory': parentKCategory,
                'limit': limit,
                'offset': offset,
                'dataSourceId': dataSourceId,
            },
            errors: {
                400: `Invalid request (bad source, non-SCX channel)`,
            },
        });
    }
    /**
     * Assign a marketplace category to an article (writes to JTL-Wawi SCX)
     * Sets the selected category for one article on an SCX channel (UPSERT `SCX.tVerkaufskanalArtikelData`).
     * The category must belong to the channel and be listable. After this, the listing generation can run.
     *
     * T7: when the category's attribute schema was never imported into Wawi (SCX lazy tree,
     * `attributesImported: false` in `GET /categories`), it is fetched SYNCHRONOUSLY from the marketplace
     * (official JTL SCX Seller API, using the seller credentials already stored in the client's Wawi) and
     * written to Wawi as part of this request — expect it to take a couple of seconds; the marketplace
     * fields are available immediately afterwards. No Wawi UI interaction needed anymore.
     *
     * If that fetch fails, NOTHING is assigned (the request is atomic in effect) and a specific error code
     * is returned: `marketplace_credentials_missing` (400), `marketplace_auth_failed` (400),
     * `marketplace_rate_limited` (429) or `marketplace_schema_fetch_failed` (502). Show the user the exact
     * message and let them retry.
     *
     * NOTE: changing the category orphans the article's existing category-specific attribute values (their
     * `cAttributeId` is reset) — they must be re-generated/re-filled for the new category.
     * @param requestBody
     * @returns any Category assigned (and its attribute schema present in Wawi)
     * @throws ApiError
     */
    public static postAppMarketplaceListingAssignCategory(
        requestBody: {
            /**
             * Channel identifier `shop:N`
             */
            source: string;
            wawiArtikelId: number;
            /**
             * SCX category id from GET /categories
             */
            kCategory: number;
            dataSourceId?: string | null;
        },
    ): CancelablePromise<{
        success?: boolean;
        /**
         * true = the article had no channel row and one was created; false = an existing row was updated
         */
        inserted?: boolean;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v2/api/marketplace-listing/assign-category',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid request (bad source/category, non-SCX channel, write failed) — or \`marketplace_credentials_missing\` / \`marketplace_auth_failed\`: the marketplace schema fetch could not authenticate; nothing was assigned`,
                429: `\`marketplace_rate_limited\` — the marketplace API throttled the schema fetch; nothing was assigned, retry in ~1 minute`,
                502: `\`marketplace_schema_fetch_failed\` — the marketplace did not deliver the category schema (network/server error); nothing was assigned, retry in a few seconds`,
            },
        });
    }
    /**
     * AI-suggest the marketplace category for an article (ticket §4)
     * Two-pass AI detection: derives German search phrases from the product, searches the channel's listable
     * categories, then ranks them and returns the best 1-3 candidates (with confidence + reason). Read-only
     * by default. Pass `assign: true` to also write the TOP candidate to Wawi (same as the generate
     * `autoDetectCategory` flag) — otherwise the user picks one and calls `POST /assign-category`.
     *
     * SCX marketplaces only. Returns an empty `suggestions` list when nothing fits.
     * @param requestBody
     * @returns MarketplaceCategorySuggestionView Ranked candidates (+ assigned flag)
     * @throws ApiError
     */
    public static postAppMarketplaceListingSuggestCategory(
        requestBody: {
            /**
             * Channel identifier `shop:N`
             */
            source: string;
            wawiArtikelId: number;
            /**
             * Active shop language: kSprache (integer or digit string) or ISO code
             */
            language: string;
            /**
             * Also assign the top candidate to Wawi. Default false.
             */
            assign?: boolean | null;
            dataSourceId?: string | null;
        },
    ): CancelablePromise<MarketplaceCategorySuggestionView> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/v2/api/marketplace-listing/suggest-category',
            body: requestBody,
            mediaType: 'application/json',
            errors: {
                400: `Invalid request (bad source/language, non-SCX channel, assign write failed)`,
                403: `\`content_master_quota_exceeded\` — the token budget cannot cover the detect price (content_master_base_tokens_category_detect × model tier)`,
            },
        });
    }
}
